/**
 * Empty state — circled icon, title, explanation, and one primary action.
 *
 * The explanation should say what to do, not just restate that the list is empty: several
 * screens have more than one empty case (no BU enabled at all vs. an enabled BU with nothing in
 * it yet) and the wording is what tells them apart.
 *
 * @module Shared/components
 */

import { css } from '@emotion/css';
import { GLIcon } from '@group-link-one/gl-icons';
import type { IGLIcon } from '@group-link-one/gl-icons';
import type { JSX, ReactNode } from 'react';

const containerStyle = css`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: var(--space-m);
  padding: var(--space-xl) var(--space-ml);
  background: var(--color-surface);
  border: 1px solid var(--color-border);
  border-radius: var(--radius-card);
  text-align: center;
`;

const iconCircleStyle = css`
  width: 48px;
  height: 48px;
  border-radius: var(--radius-round);
  background: var(--color-surface-sunken);
  display: flex;
  align-items: center;
  justify-content: center;
`;

const textStyle = css`
  display: flex;
  flex-direction: column;
  gap: 4px;
  max-width: 420px;
`;

const titleStyle = css`
  font-size: var(--font-size-md);
  font-weight: var(--font-weight-semibold);
`;

const descriptionStyle = css`
  font-size: var(--font-size-sm);
  line-height: var(--line-height-normal);
  color: var(--color-text-secondary);
  text-wrap: pretty;
`;

export interface EmptyStateProps {
  icon: IGLIcon['variant'];
  title: string;
  description: string;
  /** Primary action. Omit it when the user has no flag to act. */
  action?: ReactNode;
  id?: string;
}

export const EmptyState = ({
  icon,
  title,
  description,
  action,
  id,
}: EmptyStateProps): JSX.Element => (
  <div id={id} className={containerStyle}>
    <span className={iconCircleStyle}>
      <GLIcon variant={icon} size={22} fill='var(--color-text-muted)' />
    </span>
    <div className={textStyle}>
      <span className={titleStyle}>{title}</span>
      <span className={descriptionStyle}>{description}</span>
    </div>
    {action}
  </div>
);
